import * as React from 'react';
import {FC, useEffect} from 'react';
import {NavLink} from 'react-router-dom';
import {Button} from '~/components/Button';
import {TagsList} from '~/components/Controls/Tags';
import {onSubmitError} from '~/components/Form';
import {useModals} from '~/components/Modal';
import {Progress} from '~/components/Progress';
import style from '~/components/ResourcePage/index.module.scss';
import {TableSort, TableSortDirection} from '~/components/Table';
import {Title} from '~/components/Title';
import {useAbility} from '~/lib/ability';
import {Copy, renderBoolean} from '~/lib/helpers';
import {Intent} from '~/lib/intent';
import {ResourceLoader, useResource} from '~/lib/loader';
import {sortByBoolean, sortByNumber, sortByString} from '~/lib/sort';
import {clientsLinks} from '~/modules/Clients';
import {ClientsAbility} from '~/modules/Clients/ability';
import {fetchSellerSources} from '~/modules/Clients/api';
import {sourceRecordsFilter} from '~/modules/Clients/filter';
import {sourcesLinks} from '~/modules/Sources/links';
import {updateLimitedSource} from '~/modules/Sources/api';
import {SelfServeSourceForm} from '~/modules/Sources/forms/SelfServeSourceForm';
import {ImportPlacementsButton} from '~/modules/Sources/components/ImportPlacementsButton';

interface ISelfServeSourcesParams {
    sellerId: string;
}

export const SelfServeSellerSources: FC<ISelfServeSourcesParams> = (props) => {

    const ability = useAbility(ClientsAbility);

    const modal = useModals();

    const fetchSources = useResource(() => fetchSellerSources({sellerId: props.sellerId}));

    const sourceEdit = modal.create(SelfServeSourceForm, {
        onSubmit: (source, formikActions, close) => {
            updateLimitedSource(source).then(close).then(fetchSources.fetch).catch(onSubmitError(formikActions));
        }
    });

    return <ResourceLoader result={fetchSources} message='Loading...'>
        {({resource: source, isPending, fetch}) => (
            <div className="layout-content-box">
                <Title className={style.titleMt}
                    toolbar={<>
                        <ImportPlacementsButton key='import' onImported={fetch}/>
                        <Button intent={Intent.SUCCESS} responsive
                            key='refresh'
                            disabled={isPending}
                            leftIcon={<i className='fa fa-refresh'/>}
                            onClick={fetch}>
                        Refresh
                        </Button></>}>Sources</Title>
                <TableSort defaultSort={{
                    sort: sortByNumber,
                    field: 'id',
                    direction: TableSortDirection.DESC
                }} header columns={[
                    {
                        field: 'integrationSourceId',
                        header: 'Integration ID',
                        cellAttrs: {
                            style: {
                                maxWidth: '120px',
                                overflow: 'hidden'
                            }
                        },
                        small: true,
                        cell: s => <Copy text={s.integrationSourceId}>{s.integrationSourceId}</Copy>
                    },
                    {
                        field: 'name',
                        header: 'Name',
                        sort: sortByString,
                        cell: s => <NavLink to={sourcesLinks.source(s.id)}>{s.name}</NavLink>
                    },
                    {
                        field: 'domain',
                        header: 'Domain',
                        sort: sortByString
                    },
                    {
                        field: 'tags',
                        header: 'Tags',
                        sort: sortByString,
                        cell: s => <TagsList values={s.tags} size="small"/>,
                        short: true
                    }, {
                        field: 'tmax',
                        header: 'TMax',
                        sort: sortByNumber
                    }, {
                        field: 'active',
                        header: 'Active',
                        cell: s => renderBoolean(s.active),
                        sort: sortByBoolean
                    }, {
                        small: true,
                        cell: (s) => <Button small
                            variant="menu"
                            onClick={() => sourceEdit.show({source: s})}
                            leftIcon={<i className='fa fa-edit'/>}
                        >Edit</Button>

                    }]} records={source.filter(sourceRecordsFilter)} placeholder={isPending && <Progress/>}/>
            </div>
        )}
    </ResourceLoader>;
};
